import { AtMaturityPeriod, LOAN_AT_MATURITY_DISPLAY, type LoanSchedulePeriod } from '@src/types/OTC/loanSchedulePeriod';
import { getFieldTitle, getFieldTooltip, type FieldDefinition } from '@src/utils/fieldDefinitionUtils';
import {
  ButtonVariants,
  CardRow,
  FormGroup,
  LoanPaymentTypeEnum,
  ToggleGroup,
  type ToggleGroupOption,
} from '@talos/kyoko';
import { useMemo } from 'react';
import {
  clampAccrualPeriodToPaymentPeriod,
  isAccrualPeriodAllowedForPaymentPeriod,
  isBulletLoanPaymentType,
  isLoanSchedulePeriod,
  isPeriodicPeriod,
} from './loanPaymentType';
import {
  LOAN_PAYMENT_PERIOD_OPTIONS,
  LOAN_PAYMENT_TYPE_OPTIONS,
  type LoanPaymentTypeOption,
} from './productLayoutTokens';
import { useLoanPaymentTypeChange } from './useLoanPaymentTypeChange';

type LoanPeriodFormData = {
  loanPaymentType?: LoanPaymentTypeEnum;
  paymentPeriod?: LoanSchedulePeriod;
  accrualPeriod?: LoanSchedulePeriod;
};

type LoanPeriodFieldChange = (
  field: 'loanPaymentType' | 'paymentPeriod' | 'accrualPeriod',
  value: string | null | undefined
) => void;

interface LoanPeriodFieldsProps {
  formData: LoanPeriodFormData;
  handleFieldChange: LoanPeriodFieldChange;
  errors?: Record<string, string>;
  touched?: Record<string, boolean>;
  requiredFields?: Record<string, boolean>;
  isOpenTerm: boolean;
  testIdPrefix: string;
  paymentPeriodField: FieldDefinition;
  accrualPeriodField: FieldDefinition;
  loanPaymentTypeField: FieldDefinition;
}

export const LoanPeriodFields = ({
  formData,
  handleFieldChange,
  errors = {},
  touched = {},
  requiredFields = {},
  isOpenTerm,
  testIdPrefix,
  paymentPeriodField,
  accrualPeriodField,
  loanPaymentTypeField,
}: LoanPeriodFieldsProps) => {
  const isBullet = isBulletLoanPaymentType(formData.loanPaymentType);
  const handlePaymentTypeChange = useLoanPaymentTypeChange(handleFieldChange, formData);

  const paymentTypeOptions = useMemo((): ToggleGroupOption<LoanPaymentTypeEnum>[] => {
    return LOAN_PAYMENT_TYPE_OPTIONS.map(option => ({
      ...option,
      disabled: option.value === LoanPaymentTypeEnum.Bullet && isOpenTerm,
    }));
  }, [isOpenTerm]);

  const paymentTypeSelection: LoanPaymentTypeOption['value'] = formData.loanPaymentType ?? LoanPaymentTypeEnum.Periodic;

  const paymentPeriodValue = isPeriodicPeriod(formData.paymentPeriod) ? formData.paymentPeriod : undefined;

  const accrualPeriodOptions = useMemo((): ToggleGroupOption<LoanSchedulePeriod>[] => {
    // Periodic: Daily/Weekly/Monthly only (no At Maturity). Bullet keeps full options with At Maturity selected.
    if (isBullet) {
      return [...LOAN_PAYMENT_PERIOD_OPTIONS, { displayName: LOAN_AT_MATURITY_DISPLAY, value: AtMaturityPeriod }];
    }
    return LOAN_PAYMENT_PERIOD_OPTIONS.map(option => ({
      ...option,
      disabled: !isAccrualPeriodAllowedForPaymentPeriod(option.value, paymentPeriodValue),
    }));
  }, [isBullet, paymentPeriodValue]);

  const accrualPeriodValue = isBullet
    ? AtMaturityPeriod
    : isLoanSchedulePeriod(formData.accrualPeriod)
      ? formData.accrualPeriod
      : undefined;

  const handlePaymentPeriodChange = (value: (typeof LOAN_PAYMENT_PERIOD_OPTIONS)[number]['value']) => {
    handleFieldChange('paymentPeriod', value);
    const nextAccrual = clampAccrualPeriodToPaymentPeriod(formData.accrualPeriod, value);
    if (nextAccrual !== formData.accrualPeriod) {
      handleFieldChange('accrualPeriod', nextAccrual);
    }
  };

  return (
    <>
      <FormGroup
        label={getFieldTitle(loanPaymentTypeField)}
        tooltip={getFieldTooltip(loanPaymentTypeField)}
        error={touched.loanPaymentType ? errors.loanPaymentType : undefined}
        mb="spacing4"
      >
        <ToggleGroup
          options={paymentTypeOptions}
          value={paymentTypeSelection}
          onChange={handlePaymentTypeChange}
          orientation="horizontal"
          selectionAnimation="slide"
          defaultSelectedVariant={ButtonVariants.Priority}
          data-testid={`${testIdPrefix}-payment-type-toggle`}
        />
      </FormGroup>

      {isBullet ? (
        <CardRow
          label={getFieldTitle(paymentPeriodField)}
          value={LOAN_AT_MATURITY_DISPLAY}
          data-testid={`${testIdPrefix}-payment-period-at-maturity`}
        />
      ) : (
        <FormGroup
          label={getFieldTitle(paymentPeriodField)}
          tooltip={getFieldTooltip(paymentPeriodField)}
          required={requiredFields.paymentPeriod}
          error={touched.paymentPeriod ? errors.paymentPeriod : undefined}
          mb="spacing4"
        >
          <ToggleGroup
            options={LOAN_PAYMENT_PERIOD_OPTIONS}
            value={paymentPeriodValue}
            onChange={handlePaymentPeriodChange}
            orientation="horizontal"
            selectionAnimation="slide"
            defaultSelectedVariant={ButtonVariants.Priority}
            data-testid={`${testIdPrefix}-payment-period-toggle`}
          />
        </FormGroup>
      )}

      <FormGroup
        label={getFieldTitle(accrualPeriodField)}
        tooltip={getFieldTooltip(accrualPeriodField)}
        required={requiredFields.accrualPeriod}
        error={touched.accrualPeriod ? errors.accrualPeriod : undefined}
        mb="spacing4"
      >
        <ToggleGroup
          options={accrualPeriodOptions}
          value={accrualPeriodValue}
          onChange={value => handleFieldChange('accrualPeriod', value)}
          disabled={isBullet}
          orientation="horizontal"
          selectionAnimation="slide"
          defaultSelectedVariant={ButtonVariants.Priority}
          data-testid={`${testIdPrefix}-accrual-period-toggle`}
        />
      </FormGroup>
    </>
  );
};
